import React, { useCallback, useMemo } from 'react';
import { useNavigate, useParams, useLocation, useLoaderData } from 'react-router-dom';
import { Heading, Button, ButtonAppearance } from '@atlassian/learning-components';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { media } from '@atlaskit/primitives/responsive';

import { BGColors } from 'src/pages/LessonPage/utils/utils';
import { ScrollBehaviorTypes, setScrollBehaviour } from 'src/utils/lessonScrollBehavior';
import { getNextLesson, getOrderedLessons, NextLessonCandidate } from 'src/pages/LessonPage/children/utils/nextLessonUtils';
import { LearningProgressStatus } from 'src/services/http/progress';
import { isStandalone } from 'src/pages/LessonPage/utils/isStandalone';
import { getLearningPathsHubPart } from 'src/utils/pathUtils';
import { getSlug } from 'src/utils/slug';
import { LessonLoaderProps } from 'src/pages/LessonPage/loader';
import { getLessonUrl, withPrefix } from 'src/utils/getUrl';
import { useProgressState } from 'src/store/progress/progressStore';
import { QuickFeedbackWrapper } from 'src/pages/LessonPage/children/QuickFeedbackWrapper';

interface NextLessonProps {
  nextLessonBackground: BGColors;
  onClick: () => void;
}

export const NextLesson: React.FC<NextLessonProps> = ({ nextLessonBackground, onClick }) => {
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const [{ progressForLessons }] = useProgressState();

  const { course, lesson, learningPath } = useLoaderData() as LessonLoaderProps;

  const isStandaloneLesson = isStandalone();
  const courseSlug = getSlug(course);
  const orderedLessons = useMemo<NextLessonCandidate[]>(() => getOrderedLessons(learningPath, course), [learningPath, course]);

  const isLessonCompleted = useCallback(
    (lessonId: string): boolean =>
      progressForLessons?.lessons.find((progressLesson) => progressLesson.lessonId === lessonId)?.status ===
      LearningProgressStatus.COMPLETED,
    [progressForLessons],
  );

  const nextLessonForAuthorisedUser = useMemo(() => {
    if (!lesson || !progressForLessons || orderedLessons.length === 0) {
      return;
    }

    const currentLessonIndex = orderedLessons.findIndex((orderedLesson) => orderedLesson.lesson.sys.id === lesson.sys.id);

    if (currentLessonIndex === -1) {
      return orderedLessons.find((orderedLesson) => !isLessonCompleted(orderedLesson.lesson.sys.id));
    }

    const lessonAfterCurrent = orderedLessons
      .slice(currentLessonIndex + 1)
      .find((orderedLesson) => !isLessonCompleted(orderedLesson.lesson.sys.id));

    if (lessonAfterCurrent) {
      return lessonAfterCurrent;
    }

    return orderedLessons.slice(0, currentLessonIndex).find((orderedLesson) => !isLessonCompleted(orderedLesson.lesson.sys.id));
  }, [lesson, orderedLessons, progressForLessons, isLessonCompleted]);

  const nextLessonForUnauthorisedUser = useMemo(
    () => getNextLesson(course, lesson, learningPath),
    [course, lesson, learningPath],
  );

  const nextLessonDetails = nextLessonForAuthorisedUser || nextLessonForUnauthorisedUser || null;
  const nextLesson = nextLessonDetails?.lesson;
  const nextLessonCourseSlug = nextLessonDetails?.courseSlug || courseSlug;

  const isLastIncompleteLesson = useMemo(() => {
    if (!lesson || !progressForLessons || orderedLessons.length === 0) {
      return;
    }

    const allLessonsExceptCurrent = orderedLessons.filter((orderedLesson) => orderedLesson.lesson.sys.id !== lesson.sys.id);

    if (allLessonsExceptCurrent.length === 0) {
      return false;
    }

    const isCurrentLessonLastOne = allLessonsExceptCurrent.every((orderedLesson) =>
      isLessonCompleted(orderedLesson.lesson.sys.id),
    );

    return isCurrentLessonLastOne;
  }, [lesson, orderedLessons, progressForLessons, isLessonCompleted]);

  const areAllLessonsCompleted = useMemo(() => {
    if (!progressForLessons || orderedLessons.length === 0) {
      return;
    }

    return orderedLessons.every((orderedLesson) => isLessonCompleted(orderedLesson.lesson.sys.id));
  }, [orderedLessons, progressForLessons, isLessonCompleted]);

  const onNextLessonClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    event.preventDefault();

    const learningPathSlug = params?.pathId;
    const allLearningPathsHubPart = getLearningPathsHubPart(location.pathname);

    onClick();

    // to prevent lesson auto completion
    setScrollBehaviour(ScrollBehaviorTypes.UNSET);

    const nextLessonSlug = getSlug(nextLesson);

    nextLessonSlug &&
      navigate(
        withPrefix(
          getLessonUrl({
            lessonSlug: nextLessonSlug,
            courseSlug: nextLessonCourseSlug,
            pathSlug: learningPathSlug,
          }),
          allLearningPathsHubPart,
        ),
        { state: { isNextLessonNavigation: true } },
      );
  };

  const isNextLessonAvailable =
    !!nextLesson && !isLastIncompleteLesson && !areAllLessonsCompleted && !isStandaloneLesson;

  const handleHrefLink = useCallback((): string => {
    if (!nextLesson) {
      return '';
    }

    const learningPathSlug = params?.pathId;
    const baseUrl = getLessonUrl({
      lessonSlug: getSlug(nextLesson),
      courseSlug: nextLessonCourseSlug,
      pathSlug: learningPathSlug,
    });

    const hubPart = getLearningPathsHubPart(location.pathname);
    const url = hubPart ? `${hubPart}${baseUrl}` : baseUrl;

    return withPrefix(url);
  }, [nextLesson, nextLessonCourseSlug, params?.pathId, location.pathname]);

  if (!isNextLessonAvailable) {
    return null;
  }

  return (
    <section css={sectionContainerStyles} id="next-lesson-section" style={{ backgroundColor: nextLessonBackground }}>
      <QuickFeedbackWrapper isNextItemAvailable={!!isNextLessonAvailable} />
      {!isStandaloneLesson && (
        <div css={nextSectionContainer} data-testid="next-lesson-section">
          <p css={nextLessonHeading}>next lesson</p>
          <div css={css({ marginBottom: '16px' })}>
            <Heading level={3}>{nextLesson?.title || 'No more lessons in this course'}</Heading>
          </div>
          <ul css={lessonObjectivesStyles}>
            {nextLesson &&
              nextLesson.sectionListCollection.items.map((section) => (
                <li css={css({ marginLeft: '24px' })} key={section.id}>
                  {section.title}
                </li>
              ))}
          </ul>
          <div css={nextLessonButtonStyles}>
            <Button
              label="Go to next lesson"
              appearance={ButtonAppearance.PRIMARY}
              onClick={onNextLessonClick}
              target="_blank"
              href={handleHrefLink()}
            />
          </div>
        </div>
      )}
    </section>
  );
};

const sectionContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  paddingTop: token('space.1000'),
  paddingBottom: token('space.1000'),
  paddingLeft: token('space.250'),
  paddingRight: token('space.250'),
});

const nextSectionContainer = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  justifyContent: 'center',
  maxWidth: '608px',
  width: '100%',
});

const nextLessonHeading = css({
  fontFamily: 'Charlie Text, sans-serif',
  fontSize: '14px',
  fontWeight: 600,
  lineHeight: '18px',
  color: token('color.text.subtle'),
  marginBottom: '16px',
  textTransform: 'uppercase',
});

const lessonObjectivesStyles = css({
  fontFamily: 'Inter, sans-serif',
  fontSize: '16px',
  fontWeight: 400,
  lineHeight: '24px',
  color: token('color.text'),
  listStyleType: 'disc',
  marginTop: 0,
  marginBottom: token('space.500'),
  paddingLeft: 0,
});

const nextLessonButtonStyles = css({
  marginLeft: 'auto',

  [media.below.sm]: {
    marginRight: 'auto',
    marginLeft: 0,
  },
});
